import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { showAlert } from '../../appToast/appToastSlice';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IEditSpendAndStashRequest {
  id: string;
  savingsPercentage?: number;
  isEnabled?: boolean;
}

interface IEditSpendAndStashResponse {
  status?: boolean;
  message?: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IEditSpendAndStashResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const editSpendAndStashAsync = createAsyncThunk(
  'savings/spendAndStash/edit',
  async (
    { id, ...payload }: IEditSpendAndStashRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.patch<IEditSpendAndStashResponse>(
        `/savings/spend-and-stash-configuration/${id}`,
        payload,
        getSavingsAuthConfig(state),
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Spend & Stash configuration updated',
          type: 'drawer',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IEditSpendAndStashResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description: getApiErrorMessage(error.response?.data),
          type: 'drawer',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const editSpendAndStashSlice = createSlice({
  name: 'savings/spendAndStash/edit',
  initialState,
  reducers: {
    resetEditSpendAndStash(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(editSpendAndStashAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(editSpendAndStashAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IEditSpendAndStashResponse;
      })
      .addCase(editSpendAndStashAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IEditSpendAndStashResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetEditSpendAndStash } = editSpendAndStashSlice.actions;

export default editSpendAndStashSlice.reducer;
